import * as Sentry from '@sentry/react'
import { getAccessToken } from './client.js'

const APPSYNC_URL = import.meta.env.VITE_APPSYNC_URL

const EXPECTED_ERROR_TYPES = new Set([
  'MFA_REQUIRED',
  'ORG_NOT_FOUND',
])

/**
 * Execute a GraphQL query or mutation against the AppSync endpoint.
 * Auth is handled by injecting the Cognito ID token from the shared
 * AuthProvider — no additional auth library needed.
 *
 * @param {string} query  GraphQL document string
 * @param {object} variables
 * @returns {Promise<object>} The `data` field from the GraphQL response
 */
export async function appsyncQuery(query, variables = {}) {
  if (!APPSYNC_URL) {
    throw new Error('VITE_APPSYNC_URL is not configured')
  }

  const token = await getAccessToken()
  const res = await fetch(APPSYNC_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
    },
    body: JSON.stringify({ query, variables }),
  })

  const json = await res.json()
  if (json.errors?.length) {
    const e = json.errors[0]
    const code = e.errorType ?? 'APPSYNC_ERROR'
    // Expected, user-level conditions that the UI handles — not bugs.
    if (!EXPECTED_ERROR_TYPES.has(code)) {
      Sentry.captureException(new Error(`AppSync error: ${code}`), {
        extra: { message: e.message, errorType: e.errorType },
      })
    }
    throw { message: e.message, code }
  }
  return json.data
}
